/**
 * Tabbed dialog wrapping Export HTML, Publish to Web, and Export for NLE in a single overlay.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExportDialogContent } from './ExportDialog.tsx';
import { PublishDialogContent } from './PublishDialog.tsx';
import { NLEExportTab } from './NLEExportTab.tsx';
import { capabilities } from '@/capabilities.ts';
import styles from './ExportHubDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: Tab;
}

type Tab = 'download' | 'publish' | 'nle';

export function ExportHubDialog({ open, onClose, initialTab }: Props) {
  const showPublish = !capabilities.bridge;
  const resolvedInitial: Tab =
    initialTab && (initialTab !== 'publish' || showPublish) ? initialTab : 'download';
  const [tab, setTab] = useState<Tab>(resolvedInitial);
  const { t } = useTranslation('export');

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${tab === 'download' ? styles.tabActive : ''}`}
              onClick={() => setTab('download')}
            >
              {t('tabDownloadHtml')}
            </button>
            {showPublish && (
              <button
                className={`${styles.tab} ${tab === 'publish' ? styles.tabActive : ''}`}
                onClick={() => setTab('publish')}
              >
                {t('tabPublishToWeb')}
              </button>
            )}
            <button
              className={`${styles.tab} ${tab === 'nle' ? styles.tabActive : ''}`}
              onClick={() => setTab('nle')}
            >
              {t('nleTabTitle')}
            </button>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.body}>
          {tab === 'download' && <ExportDialogContent onClose={onClose} />}
          {tab === 'publish' && showPublish && <PublishDialogContent onClose={onClose} />}
          {tab === 'nle' && <NLEExportTab onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}
